import { formatCompactNumber, formatNumberWithCommas } from "../formatting/format-number";
import { parsePrizePool, parsePrizePoolAmount, type ParsedPrizePool } from "./prize-pool";

// Glyphs that render as a prefix ("$1,000"); word symbols (USDC, points) render as a suffix.
const PREFIX_GLYPHS = new Set(["$", "€", "£", "¥", "₿"]);

export type RewardParts = {
  prefix: string;
  amount: number | null; // null when the value couldn't be parsed
  suffix: string; // word symbols, leading space
  raw: string; // preserved for the unparseable fallback
};

// Resolve a reward value + symbol into display parts. Number or free-form string ("$10,000" -> 10000).
// When the amount can't be parsed, `amount` is null and callers render `raw`.
export function buildRewardParts(value: number | string | null | undefined, symbol: string): RewardParts {
  const sym = (symbol ?? "").trim() || "$";
  const amount =
    typeof value === "number" ? (Number.isFinite(value) ? value : null) : parsePrizePoolAmount(value ?? null);
  const raw = typeof value === "string" ? value : amount != null ? String(amount) : "";
  if (amount == null) return { prefix: "", amount: null, suffix: "", raw };
  return PREFIX_GLYPHS.has(sym)
    ? { prefix: sym, amount, suffix: "", raw }
    : { prefix: "", amount, suffix: ` ${sym}`, raw };
}

// Format a reward with its symbol: formatReward(1000, "$") -> "$1,000", (1000, "USDC") -> "1,000 USDC".
// Falls back to the raw string when unparseable; `{ compact: true }` gives the "$1.2M" form.
export function formatReward(
  value: number | string | null | undefined,
  symbol: string,
  options?: { compact?: boolean },
): string {
  const { prefix, amount, suffix, raw } = buildRewardParts(value, symbol);
  if (amount == null) return raw;
  const formatted = options?.compact ? formatCompactNumber(amount) : formatNumberWithCommas(amount);
  return `${prefix}${formatted}${suffix}`;
}

// Symbol-aware variant of parsePrizePool for the Flow animated-number components.
// Returns `null` when no amount can be recovered, so callers fall back to the raw label.
export function parseRewardWithSymbol(
  value: number | string | null | undefined,
  symbol: string,
): ParsedPrizePool | null {
  const sym = (symbol ?? "").trim() || "$";
  let amount: number | null;
  let isCompact = false;
  if (typeof value === "number") {
    amount = Number.isFinite(value) ? value : null;
  } else {
    const parsed = parsePrizePool(value ?? "");
    amount = parsed?.amount ?? null;
    isCompact = parsed?.isCompact ?? false;
  }
  if (amount == null) return null;
  const isGlyph = PREFIX_GLYPHS.has(sym);
  return { prefix: isGlyph ? sym : "", amount, suffix: isGlyph ? "" : sym, isCompact };
}
